/**
 * 移动端安全巡检平台 · API 封装
 * =====================================================
 * 免登录设计：巡检云端只读接口已开放（无需 token），直接调用；
 * 「上报信息」模块（信息收集后台）为可选数据源：默认未配置 VITE_API_BASE 时隐藏；
 * 若配置了该后台，因其中仍有登录校验，进入该模块时按页面提示单独输入账号即可。
 */

const INSPECT_BASE = (import.meta.env.VITE_INSPECT_API || 'https://safety-inspection.tuzi-dx.online').replace(/\/+$/, '')
const GATHER_BASE = (import.meta.env.VITE_API_BASE || '').replace(/\/+$/, '')

export const inspectBase = INSPECT_BASE
export const gatherBase = GATHER_BASE

const TOKEN_KEY_GATHER = 'patrol_gather_token'
const USER_KEY = 'patrol_user'

export function getGatherToken() { return localStorage.getItem(TOKEN_KEY_GATHER) || '' }
export function setGatherToken(t) { localStorage.setItem(TOKEN_KEY_GATHER, t) }
export function setUserName(n) { localStorage.setItem(USER_KEY, n) }
export function getUserName() { return localStorage.getItem(USER_KEY) || '查看者' }
export function clearGatherToken() { localStorage.removeItem(TOKEN_KEY_GATHER) }

/* ---------------- 通用请求 ---------------- */

async function request(base, url, options = {}, { token = '', throw401 = true } = {}) {
  if (!base) throw new Error('后台地址未配置（构建时设置 VITE_API_BASE）')
  const headers = { ...(options.headers || {}) }
  if (token) headers.Authorization = `Bearer ${token}`
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  }
  let response
  try {
    response = await fetch(`${base}${url}`, { ...options, headers })
  } catch (e) {
    // 浏览器跨域被拒 / 域名不可达 / 未部署时 fetch 抛 TypeError
    throw new Error(e instanceof TypeError ? '无法连接服务器（Failed to fetch）' : '网络错误：' + (e.message || '无法连接后台'))
  }
  if (response.status === 401 && throw401) {
    // Token 已被后端拒绝（旧版未签名 Token / 已过期）：先清除本地死 Token，避免后续请求继续携带。
    // 仅当本次请求确实带了 Token 才清除——巡检云端接口是免登录只读的（不带 token），
    // 不能让它的 401 误清掉「信息上报」后台的登录态。
    if (token) clearGatherToken()
    // 服务端若以 JSON 返回具体原因则优先展示；该后台 401 多为纯文本，此时用统一提示
    let msg = ''
    try {
      const j = JSON.parse(await response.text().catch(() => ''))
      msg = (j && j.message) || ''
    } catch { /* 非 JSON 响应，保留统一提示 */ }
    const err = new Error(msg || '登录已失效，请重新输入账号密码')
    err.authExpired = true // 标记登录态失效，供 isAuthError 判断（与提示文案解耦）
    throw err
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    let msg = text
    try {
      const j = JSON.parse(text)
      msg = j.message || j.error || text
    } catch { /* 保留原文 */ }
    throw new Error(msg || `请求失败: ${response.status}`)
  }
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) return response.json()
  return response
}

/* ==================== 安全巡检云端（免登录只读） ==================== */

/** 总览统计 */
export function fetchOverview() {
  return request(INSPECT_BASE, '/api/overview', { method: 'GET' })
}

/** 巡检计划 / 巡检安排 / 值班领导 */
export function fetchSchedule() {
  return request(INSPECT_BASE, '/api/schedule', { method: 'GET' })
}

/** 异常/隐患列表：all=1 全部；light=1 轻量（不含照片base64） */
export function fetchHazards(params = {}) {
  const q = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v)
  })
  return request(INSPECT_BASE, `/api/hazards?${q.toString()}`, { method: 'GET' })
}

/** 隐患照片/视频（单条按需加载） */
export function fetchHazardMedia(id) {
  return request(INSPECT_BASE, `/api/hazard-media?id=${encodeURIComponent(id)}`, { method: 'GET' })
}

/** 巡检记录轻量列表 */
export function fetchInspectRecordsSlim(params = {}) {
  const q = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v)
  })
  return request(INSPECT_BASE, `/api/records?${q.toString()}`, { method: 'GET' })
}

/** 巡检记录单条轻量详情（不含照片base64） */
export function fetchInspectRecord(id) {
  return request(INSPECT_BASE, `/api/record?id=${encodeURIComponent(id)}`, { method: 'GET' })
}

/** 巡检记录完整媒体（点击「加载照片」后再取） */
export function fetchInspectRecordMedia(id) {
  return request(INSPECT_BASE, `/api/record-media?id=${encodeURIComponent(id)}`, { method: 'GET' })
}

/* ==================== 信息收集后台（可选「上报信息」模块） ==================== */

export function gatherEnabled() { return !!GATHER_BASE }

/** 登录信息收集后台（仅在进入上报信息模块时按需使用） */
export function loginGather(credentials) {
  return request(GATHER_BASE, '/api/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }, {
    // 登录接口的 401 表示账号或密码错误，不能当作「登录失效」处理：
    // 关闭 401 专用分支后由通用错误分支抛出服务端原文，并保留原有 Token
    throw401: false
  })
}

/** 上报记录列表 */
export function fetchGatherRecords(params = {}) {
  const q = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v)
  })
  return request(GATHER_BASE, `/api/list?${q.toString()}`, { method: 'GET' }, { token: getGatherToken() })
}

/** 更新上报记录处理状态 */
export function updateGatherStatus(id, status) {
  return request(GATHER_BASE, '/api/update', {
    method: 'POST',
    body: JSON.stringify({ id, status })
  }, { token: getGatherToken() })
}

/** 上报记录媒体地址 */
export function getGatherImageUrl(id, index = 0) {
  return `${GATHER_BASE}/api/image?id=${id}&index=${index}&token=${encodeURIComponent(getGatherToken())}`
}
export function getGatherVideoUrl(id, index = 0) {
  return `${GATHER_BASE}/api/video?id=${id}&index=${index}&token=${encodeURIComponent(getGatherToken())}`
}
export function getGatherDownloadUrl(id, index = 0) {
  return `${GATHER_BASE}/api/download?id=${id}&index=${index}&token=${encodeURIComponent(getGatherToken())}`
}

/** 判断错误是否提示需要登录（Token 失效/过期，或后端要求登录） */
export function isAuthError(e) {
  if (e && e.authExpired) return true
  const m = (e && e.message) || ''
  return m.includes('登录已失效') || m.includes('重新登录') || m.includes('需要登录') || m.includes('401')
}
